import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { ISelfReportDetailed } from 'src/app/interfaces/SelfReport/ISelfReportDetailed';
import { SelfReportService } from 'src/app/services/selfReport.service';
import { date_format, datetime_format } from 'src/date/date.configuration';

@Component({
  selector: 'app-self-report',
  templateUrl: './self-report.component.html',
  styleUrls: ['./self-report.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelfReportComponent {
  bodySide: 'front' | 'back' = 'front';
  selectedFrontBodyParts: string[] = [];
  selectedBackBodyParts: string[] = [];

  private destroy$ = new Subject<void>();

  selfReports?: ISelfReportDetailed[];
  isLoading: boolean = false;

  selectedSelfReportId?: number;

  currentPageIndex: number = 1;
  pageSize: number = 5;

  constructor(
    private readonly selfReportService: SelfReportService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.selfReportService.selfReportLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
        this.cdr.detectChanges();
      });

    this.selfReportService.selfReportDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ISelfReportDetailed[]) => {
        this.selfReports = data;
        this.selectedSelfReportId = data?.at(0)?.id;
        this.selectedFrontBodyParts = [
          ...(data?.at(0)?.selectedBodyParts?.front || []),
        ];
        this.selectedBackBodyParts = [
          ...(data?.at(0)?.selectedBodyParts?.back || []),
        ];
        this.selfReports =
          this.selfReports?.map((sr) => ({
            ...sr,
            description:
              sr.description != undefined &&
              sr.description != null &&
              sr.description != ''
                ? sr.description
                : '-',
            front:
              sr.selectedBodyParts.front.length > 0
                ? sr.selectedBodyParts.front.map(
                    (part) => part.charAt(0).toUpperCase() + part.slice(1)
                  )
                : ['-'],
            back:
              sr.selectedBodyParts.back.length > 0
                ? sr.selectedBodyParts.back.map(
                    (part) => part.charAt(0).toUpperCase() + part.slice(1)
                  )
                : ['-'],
          })) || [];

        this.cdr.detectChanges();
      });

    this.selfReportService.getData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // onPartClicked(slug: string) {
  //   const index = this.selectedBodyParts.indexOf(slug);
  //   if (index > -1) {
  //     this.selectedBodyParts.splice(index, 1);
  //   } else {
  //     this.selectedBodyParts.push(slug);
  //   }
  // }

  getSelectedIndex(): number {
    return (
      this.selfReports?.findIndex(
        (sr) => sr.id === this.selectedSelfReportId
      ) ?? -1
    );
  }

  formatSelectedDate(): string {
    const selected = this.selfReports?.find(
      (sr) => sr.id === this.selectedSelfReportId
    );
    return selected?.creationDateTime
      ? moment(selected.creationDateTime).format(datetime_format)
      : '';
  }

  goBack() {
    const currentIndex = this.selfReports?.findIndex(
      (sr) => sr.id === this.selectedSelfReportId
    );
    if (currentIndex !== undefined && currentIndex > 0) {
      const newId = this.selfReports?.at(currentIndex - 1)?.id;
      if (newId !== undefined) {
        this.changePage(currentIndex - 1);
        this.changeView(newId);
      }
    }
  }

  goForth() {
    const currentIndex = this.selfReports?.findIndex(
      (sr) => sr.id === this.selectedSelfReportId
    );
    if (
      this.selfReports &&
      currentIndex !== undefined &&
      currentIndex < this.selfReports.length - 1
    ) {
      const newId = this.selfReports?.at(currentIndex + 1)?.id;
      if (newId !== undefined) {
        this.changePage(currentIndex + 1);
        this.changeView(newId);
      }
    }
  }

  changePage(index: number) {
    const page = Math.floor(index / this.pageSize) + 1;
    if (page !== this.currentPageIndex) {
      this.currentPageIndex = page;
      this.cdr.detectChanges();
    }
  }

  isIndexOnCurrentPage(index: number): boolean {
    const start = (this.currentPageIndex - 1) * this.pageSize;
    const end = this.currentPageIndex * this.pageSize;
    return index >= start && index < end;
  }

  changeView(id: number) {
    this.selectedSelfReportId = id;

    const sr = this.selfReports?.find((sr) => sr.id === id);
    this.selectedFrontBodyParts = [...(sr?.selectedBodyParts.front || [])];
    this.selectedBackBodyParts = [...(sr?.selectedBodyParts.back || [])];

    this.cdr.detectChanges();
  }

  private dragStartX: number | null = null;

  startDrag(event: MouseEvent) {
    this.dragStartX = event.clientX;
  }

  onDrag(event: MouseEvent) {
    if (this.dragStartX !== null) {
      const delta = event.clientX - this.dragStartX;
      if (Math.abs(delta) > 50) {
        if (delta > 0) this.goForth();
        else this.goBack();
        this.dragStartX = null; // prevent repeated triggers
      }
    }
  }

  endDrag(event: MouseEvent) {
    this.dragStartX = null;
  }

  getData() {
    const startIndex = (this.currentPageIndex - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this?.selfReports?.slice(startIndex, endIndex) || [];
  }

  handlePageIndexChange(pageIndex: number): void {
    this.currentPageIndex = pageIndex;
  }

  handlePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPageIndex = 1; // Reset to the first page when changing page size
  }

  getValue(field: string, value?: string) {
    if (!value) {
      return '-';
    }

    if (field?.toLowerCase()?.includes('datetime')) {
      return moment(value).format(datetime_format);
    } else if (field?.toLowerCase()?.includes('date')) {
      return moment(value).format(date_format);
    } else {
      return value;
    }
  }
}
